import { Button, CircularProgress, Link, TextField } from '@mui/material'
import { signup, signInWithGoogle } from 'common/saga-actions/authActions'
import Alert from 'common/components/alert/Alert'
import { validateEmail, validatePassword, validateNotEmpty } from 'common/utils/validate'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import LoadingButton from '@mui/lab/LoadingButton'
import useLocale from 'common/hooks/useLocale'
import Disclaimer from 'common/screens/auth/Disclaimer'
import { GoogleIcon } from 'common/icons'
import { useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import { DOMAIN_URL } from 'common/config/domain'

export default function Signup({ loginLink }) {
	const dispatch = useDispatch()
	const { t } = useTranslation('common')
	const { locale } = useLocale()
	const navigate = useNavigate()

	// Props from redux store
	const loading = useSelector((state) => state.global.loading?.login)

	// State
	const [form, setForm] = useState({ email: '', password: '', name: '', surname: '' })
	const [validation, setValidation] = useState({
		email: { valid: true, error: '' },
		password: { valid: true, error: '' },
		name: { valid: true, error: '' },
		surname: { valid: true, error: '' }
	})

	function onChange(key, text) {
		setForm({ ...form, [key]: text })
	}

	async function onSubmit(event) {
		event.preventDefault()
		const nameValid = validateNotEmpty(form.name)
		const surnameValid = validateNotEmpty(form.surname)
		const emailValid = validateEmail(form.email)
		const passwordValid = validatePassword(form.password)
		setValidation({
			...validation,
			name: nameValid,
			surname: surnameValid,
			email: emailValid,
			password: passwordValid
		})
		if (nameValid.valid && surnameValid.valid && emailValid.valid && passwordValid.valid) {
			dispatch(signup({ name: form.name, surname: form.surname, email: form.email, password: form.password, locale }))
		}
	}

	async function onGoogleSignUp() {
		dispatch(signInWithGoogle(locale))
	}

	function onLogin() {
		if (loginLink) loginLink()
		else navigate('/login')
	}

	return (
		<div className="h-full w-full bg-gray-50 flex flex-col justify-center items-center p-4 md:p-0">
			<Helmet>
				<title>{t('signup.title')} | Singularly</title>
				<link rel="canonical" href={`${DOMAIN_URL}/signup`} />
			</Helmet>
			<div className="paper p-8 flex flex-col items-center w-full md:w-2/3 2xl:w-1/3">
				<Link href="https://singularly.com" target="_blank" rel="noopener">
					<img src="/logo-positive.png" alt="Logo" width="200" height="200" />
				</Link>
				<p className="text-2xl pb-4">{t('signup.title')}</p>

				<div className="w-full">
					<Alert messageKey="login" className="mb-4" />
				</div>

				{/* <Button
					onClick={onGoogleSignUp}
					fullWidth
					variant="outlined"
					className="bg-white text-black buttonOutlinedGray normal-case"
					disabled={loading}
					startIcon={<GoogleIcon className="googleIcon w-[20px] h-[20px]" />}
				>
					{t('login.button_with_google')}
				</Button>

				<div className="border-b border-borderGray leading-[0] my-10 w-full text-center">
					<span className="px-5 bg-white text-textGray text-sm leading-[0] lowercase">{t('common:login.or')}</span>
				</div> */}

				<TextField
					className="pb-4"
					autoComplete="fname"
					name="firstName"
					variant="outlined"
					margin="none"
					size="small"
					required
					fullWidth
					id="firstName"
					label={t('signup.first_name')}
					autoFocus
					onChange={(event) => onChange('name', event.target.value)}
					inputProps={{ className: 'text-sm' }}
					value={form.name}
					error={!validation.name.valid}
					helperText={
						!validation.name.valid &&
						t(validation.name.error, {
							field: t('signup.first_name')
						})
					}
				/>
				<TextField
					className="pb-4"
					variant="outlined"
					margin="none"
					size="small"
					required
					fullWidth
					id="lastName"
					label={t('signup.last_name')}
					name="lastName"
					autoComplete="lname"
					onChange={(event) => onChange('surname', event.target.value)}
					inputProps={{ className: 'text-sm' }}
					value={form.surname}
					error={!validation.surname.valid}
					helperText={
						!validation.surname.valid &&
						t(validation.surname.error, {
							field: t('signup.last_name')
						})
					}
				/>
				<TextField
					className="pb-4"
					variant="outlined"
					margin="none"
					size="small"
					required
					fullWidth
					id="email"
					label={t('login.email')}
					name="email"
					autoComplete="email"
					onChange={(event) => onChange('email', event.target.value)}
					inputProps={{ className: 'text-sm' }}
					value={form.email}
					error={!validation.email.valid}
					helperText={
						!validation.email.valid &&
						t(validation.email.error, {
							field: t('login.email')
						})
					}
				/>
				<TextField
					className="pb-4"
					variant="outlined"
					margin="none"
					size="small"
					required
					fullWidth
					name="password"
					label={t('login.password')}
					type="password"
					id="password"
					autoComplete="current-password"
					onChange={(event) => onChange('password', event.target.value)}
					inputProps={{ className: 'text-sm' }}
					value={form.password}
					error={!validation.password.valid}
					helperText={
						!validation.password.valid &&
						t(validation.password.error, {
							field: t('login.password')
						})
					}
				/>

				<LoadingButton
					loading={loading}
					variant="contained"
					fullWidth
					className="buttonGradient mb-4 shadow-none"
					onClick={onSubmit}
					loadingIndicator={<CircularProgress size={20} className="text-white" />}
				>
					{t('signup.button')}
				</LoadingButton>

				<div className="flex flex-row justify-between w-full mb-6">
					<Link className="text-black text-sm no-underline hover:underline cursor-pointer" onClick={onLogin}>
						{t('signup.already_account')}
					</Link>
				</div>
				<Disclaimer isSignUp={true} />
			</div>
		</div>
	)
}
